import Head from "next/head";
import Script from "next/script";

import axios from "axios";

import Footer from "@/components/marketing/pieces/Footer";
import NavbarLegacy from "@/components/marketing/pieces/NavbarLegacy";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import CompetitorComparisonTable from "@/components/seo/CompetitorComparisonTable";
import FAQs from "@/components/seo/FAQs";
import MainCallToAction from "@/components/seo/MainCallToAction";
import PageHero from "@/components/seo/PageHero";
import SuccessStories from "@/components/seo/SuccessStories";
import TestimonialMarquee from "@/components/seo/TestimonialMarquee";
import WebsitePreview from "@/components/seo/WebsitePreview";
import WhatYouGetSection from "@/components/seo/WhatYouGetSection";
import { industryBlurbs } from "@/lib/web-design-industry-blurbs";
import { SITE_DOMAIN } from "@/lib/constants";
import { DURATIONS } from "@/lib/constants/durations";
import { defaultIndustryList, INDUSTRY_SLUGS, INDUSTRY_SUGGESTIONS_BASE_URL } from "@/lib/constants/industries";
import { PRICING_PLANS } from "@/lib/constants/pricing";
import { logError } from "@/lib/utils";

interface IndustryPageProps {
  industry: string;
  industrySlug: string;
  industryServices: {
    label: string;
    description?: string;
  }[];
  industryBlurb: string;
}

async function getIndustryServices(industrySlug: string) {
  const googleCategory = defaultIndustryList.find((ind) => ind.slug === industrySlug)?.googleCategory;
  const response = await axios.get(
    `${INDUSTRY_SUGGESTIONS_BASE_URL}${encodeURIComponent(googleCategory || "")}/services`
  );
  const { services } = response.data;
  return (services || [])
    .map((service: { name: string }) => ({
      label: service.name,
    }))
    .sort(() => Math.random() - 0.5)
    .slice(0, 6);
}

export async function getStaticProps({ params }: { params: { industrySlug: string } }) {
  try {
    const { industrySlug } = params;

    const normalizedSlug = industrySlug.toLowerCase();

    const industryMatch = INDUSTRY_SLUGS.find(({ slug }) => slug === normalizedSlug);

    if (!industryMatch) {
      return {
        notFound: true,
      };
    }

    const industryBlurb = industryBlurbs[normalizedSlug as keyof typeof industryBlurbs] ?? "";

    const industryServices = await getIndustryServices(industrySlug);

    return {
      props: {
        industry: industryMatch.label,
        industrySlug: normalizedSlug,
        industryServices,
        industryBlurb,
      },
      revalidate: DURATIONS.ONE_WEEK,
    };
  } catch (error) {
    logError("Error fetching [industrySlug] page", error);
    return {
      notFound: true,
      revalidate: DURATIONS.ONE_DAY, // revalidate sooner if page error
    };
  }
}

export async function getStaticPaths() {
  const paths = INDUSTRY_SLUGS.map(({ slug: industrySlug }) => ({
    params: {
      industrySlug,
    },
  }));

  return {
    paths,
    fallback: false,
  };
}

export default function IndustryPage({ industry, industrySlug, industryServices, industryBlurb }: IndustryPageProps) {
  const lowercaseIndustry = industrySlug === "hvac" ? industry : industry.toLowerCase();

  const pageTitle = `Top-Rated ${industry} Web Design Company | FREE Consultation`;
  const metaDescription = `Award-winning ${lowercaseIndustry} websites that drive real results. Join 300+ satisfied businesses who trust our proven web design services. Free consultation available.`;

  const primaryImageUrl = `https://ik.imagekit.io/4wu305uo4/${industrySlug}/job-progress/${industrySlug}2.png`;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_DOMAIN}/web-design/${industrySlug}`} />
        <meta property="og:image" content={primaryImageUrl} />
        <meta property="og:site_name" content="Rebolt" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={primaryImageUrl} />

        <meta
          name="keywords"
          content={`${industry} web design, ${industry} website, ${industry} web designer, ${industry} web development`}
        />

        <link rel="canonical" href={`${SITE_DOMAIN}/web-design/${industrySlug}`} />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://ik.imagekit.io" />
        <link rel="dns-prefetch" href="https://ik.imagekit.io" />
      </Head>

      <Script
        id="schema-script"
        type="application/ld+json"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: pageTitle,
            description: metaDescription,
            provider: {
              "@type": "LocalBusiness",
              name: "Rebolt",
              description: `Professional ${industry} web design services`,
              addressCountry: "US",
            },
            serviceType: `${industry} Web Design`,
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "USD",
              priceRange: `$${PRICING_PLANS.basic.price}-$${PRICING_PLANS.pro.price}/month`,
              availability: "https://schema.org/InStock",
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${SITE_DOMAIN}/web-design/${industrySlug}`,
            },
          }),
        }}
      />

      <NavbarLegacy />

      <main className="overflow-hidden -mb-5">
        <Breadcrumbs className="bg-ash-100 pt-[110px]" industry={industry} industrySlug={industrySlug} />
        <PageHero
          industry={industry}
          industrySlug={industrySlug}
          primaryImageUrl={primaryImageUrl}
          industryBlurb={industryBlurb}
        />
        <TestimonialMarquee className="pb-24" />
        <WhatYouGetSection industry={industry} />
        <SuccessStories industry={industry} />
        <WebsitePreview industry={industry} industryServices={industryServices} />
        <CompetitorComparisonTable industry={industry} />
        <MainCallToAction industry={industry} backgroundImage={primaryImageUrl} />
        <FAQs industry={industry} />
      </main>
      <Footer />
    </>
  );
}
